import React, { useState } from "react";
import { Clock, Lightbulb } from "lucide-react";
import CourseDetailsModal from "./CourseDetailsModal";
import DropDown from "../dropDown/DropDown";

const CourseCard = ({ pathwayData }) => {
  const courses = pathwayData?.courses || [];
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [courseStatuses, setCourseStatuses] = useState({});
  
  const modulesData = courses.map((course, index) => {
    const courseId = course.id || `course-${index}`;
    return {
      id: courseId,
      number: index + 1,
      title: course.title,
      description: course.description,
      reason: course.reason,
      difficulty: course.difficulty,
      difficultyColor: course.difficulty === "Beginner" ? "text-green-600" : 
                       course.difficulty === "Intermediate" ? "text-orange-600" : "text-red-600",
      status: courseStatuses[courseId] || course.status || "Not Started",
      link: course.link,
      skills: course.skills || ["AI Literacy", "Machine Learning Basics", "Data Analysis"],
      duration: course.duration || "2 weeks",
      courseType: course.courseType || "Course",
      recommendationReason: course.recommendationReason || course.reason
    };
  });

  const handleViewDetails = (module) => {
    setSelectedCourse(module);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCourse(null);
  };

  const statusOptions = [
    { value: "Not Started", label: "Not Started" },
    { value: "Started", label: "Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" }
  ];

  const handleStatusChange = (courseId, newStatus) => {
    setCourseStatuses(prev => ({
      ...prev,
      [courseId]: newStatus
    }));
  };

  if (modulesData.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No courses available at the moment.</p>
      </div>
    );
  }

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: "bg-green-100 text-green-800",
      intermediate: "bg-yellow-100 text-yellow-800", 
      advanced: "bg-red-100 text-red-800",
    };
    return colors[difficulty?.toLowerCase()] || colors.beginner;
  };

  return (
    <>
      <div className="space-y-4">
        {modulesData.map((module, index) => (
        <div key={index} className=" border border-gray-400 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-100 mt-5 text-blue-900 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
              {module.number}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-1">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-blue-900 leading-tight mb-1">
                    {module.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {module.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(module.difficulty)}`}>
                    {module.difficulty}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-1 mb-4">
                <Lightbulb className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Reason for Recommendation:</span> {module.reason}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <button 
                  onClick={() => handleViewDetails(module)}
                  className="text-sm font-medium text-gray-700 border border-gray-400 px-5 py-1 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                >
                  View Details
                </button>
                 <div className="flex items-center gap-4">
                   <span className="text-sm font-semibold text-gray-700">Update your Status</span>
                   <div className="w-40">
                     <DropDown
                       options={statusOptions}
                       defaultValue={module.status}
                       onChange={(selectedOption) => handleStatusChange(module.id, selectedOption.value)}
                       placeholder="Select Status"
                       className="text-sm"
                       hideClear={true}
                     />
                   </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      </div>
      
      <CourseDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        course={selectedCourse}
      />
    </>
  );
};

export default CourseCard; 